import { extractIsobars } from './helpers';

export const NUMERIC_KEYWORDS = ['Chains', 'ValidChains', 'InvalidChains'];
export const OPERATORS = ['>', '<', '=', '>=', '<='];

export function compare(a, op, b) {
    switch (op) {
        case '>':  return a > b;
        case '<':  return a < b;
        case '=':  return a === b;
        case '>=': return a >= b;
        case '<=': return a <= b;
        default:   return false;
    }
}

export function getTopologyStats(topology, globalIsobars, checkedGraphs) {
    const isobars = {};
    extractIsobars(topology[0], isobars);
    extractIsobars(topology[1], isobars);
    const isobarKeys = Object.keys(isobars);
    const chains = isobarKeys.length === 0 ? 0 : isobarKeys.reduce((acc, key) => {
        const count = Object.keys(globalIsobars?.[key]?.resonances || {}).length;
        return acc * count;
    }, 1);
    const topStr = JSON.stringify(topology);
    const trees = checkedGraphs?.trees?.filter(t => t.topology === topStr) || [];
    return {
        Chains: chains,
        ValidChains: trees.filter(t => t.check).length,
        InvalidChains: trees.filter(t => !t.check).length,
    };
}

export function applyClientFilters(topologies, clientFilters, globalIsobars, checkedGraphs) {
    if (!clientFilters?.length) return topologies;
    return topologies.filter(topology => {
        const stats = getTopologyStats(topology, globalIsobars, checkedGraphs);
        return clientFilters.every(f => compare(stats[f.type] ?? 0, f.op, f.value));
    });
}

export function parseInput(input, finalStateData, finalStateNodes) {
    const trimmed = input.trim();
    const parts = trimmed.split(/\s+/).filter(Boolean);
    const endsWithSpace = input.length > 0 && input !== trimmed;

    if (!parts.length) {
        return {
            mode: 'idle', canCommit: false,
            suggestions: [
                ...(finalStateNodes || []).map(n => ({ type: 'particle', node: n, label: finalStateData?.[n]?.name })),
                ...NUMERIC_KEYWORDS.map(k => ({ type: 'keyword', label: k })),
            ],
        };
    }

    const firstLower = parts[0].toLowerCase();
    const exactKeyword = NUMERIC_KEYWORDS.find(k => k.toLowerCase() === firstLower);
    const partialKeywords = NUMERIC_KEYWORDS.filter(k => k.toLowerCase().startsWith(firstLower));

    // Numeric filter flow
    if (exactKeyword && (parts.length > 1 || endsWithSpace)) {
        const opStr = parts[1] || '';
        const exactOp = OPERATORS.find(o => o === opStr);
        if (exactOp && (parts.length > 2 || endsWithSpace)) {
            const num = parseInt(parts[2] || '');
            if (!isNaN(num)) {
                return { mode: 'complete', keyword: exactKeyword, op: exactOp, value: num, canCommit: true, suggestions: [] };
            }
            return { mode: 'number', keyword: exactKeyword, op: exactOp, canCommit: false, suggestions: [] };
        }
        const matchingOps = opStr ? OPERATORS.filter(o => o.startsWith(opStr)) : OPERATORS;
        return {
            mode: 'operator', keyword: exactKeyword, canCommit: false,
            suggestions: matchingOps.map(o => ({ type: 'operator', label: o })),
        };
    }

    // Particle / partial keyword suggestions
    const currentQuery = (parts[parts.length - 1] || '').toLowerCase();
    const matchingParticles = (finalStateNodes || []).filter(n =>
        finalStateData?.[n]?.name?.toLowerCase().includes(currentQuery)
    );
    return {
        mode: 'particles', canCommit: false,
        suggestions: [
            ...partialKeywords.map(k => ({ type: 'keyword', label: k })),
            ...matchingParticles.map(n => ({ type: 'particle', node: n, label: finalStateData?.[n]?.name })),
        ],
    };
}
